import { useEffect, useRef, useState } from "react";
import { FaHome, FaMoneyCheckAlt } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import { GiHamburgerMenu } from "react-icons/gi";
import { MdComputer, MdOutlineMiscellaneousServices } from "react-icons/md";
import { useRecoilValue } from "recoil";
import { SidebarLink } from "../components/SidebarLink";
import { feedbackTypes } from "../constants/feedbackTypes";
import { userData } from "../recoil/atom/userData";
import { privateRoutes } from "../routes/privateRoutes";
import PropTypes from 'prop-types';

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const trigger = useRef(null);
  const sidebar = useRef(null);

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const user = useRecoilValue(userData);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current?.contains(target) ||
        trigger.current?.contains(target)
      )
        return;
      setSidebarOpen(true);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, []);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(true);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, []);

  useEffect(() => {
    if (sidebarExpanded) {
      document?.querySelector("body").classList.add("sidebar-expanded");
    } else {
      document?.querySelector("body").classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  // Dynamic dashboard menus based on user.feedbackType
  const [feedbackDashboards, setFeedbackDashboards] = useState([]);

  useEffect(() => {
    setFeedbackDashboards(
      (user?.feedbackTypes || []).map((type) => ({
        path: type,
        label: `${feedbackTypes[type]} Dashboard`,
        icon: getIcon(type),
        showInSideBar: true,
      }))
    );
  }, [user]);

  const getIcon = (type) => {
    switch (type) {
      case "sales":
        return <FaMoneyCheckAlt />;
      case "digital_lead":
        return <MdComputer />;
      case "enquiry":
        return <FaCircleInfo />;
      case "service":
        return <MdOutlineMiscellaneousServices />;
      default:
        return <FaHome />;
    }
  };

  return (
    <div>
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      ></div>
      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 transform h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:!w-64 shrink-0 bg-[#1D2637] p-4 transition-all duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-64"
        }`}
      >
        {/* Sidebar backdrop (mobile only) */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden transition-opacity duration-200 ${
            sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden="true"
        ></div>
        {/* Sidebar header */}
        <div className="flex items-center justify-between mb-2 pr-3 sm:px-2">
          {/* Logo */}

          <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>
            <GiHamburgerMenu className="text-slate-200 mr-2 cursor-pointer text-xl" />
          </button>
        </div>
        {/* Links */}
        <div className="space-y-8">
          <div>
            <ul className="mt-3">
              {/* Dynamic FeedbackType Dashboards */}
              {feedbackDashboards?.map((menu, index) => (
                <SidebarLink
                  key={index}
                  path={menu.path}
                  icon={menu.icon}
                  label={menu.label}
                />
              ))}
              {/* Other static/private routes, skipping "Dashboard" to avoid duplicates */}
              {privateRoutes
                .filter((menu) => {
                  // Exclude static 'Dashboard' since it's handled dynamically
                  if (menu.label === "Dashboard") return false;

                  // Show 'Whatsapp Onboarding' only if enabled for user
                  if (menu.label === "Whatsapp Onboarding") {
                    return user?.enableWhatsappRegistration === true;
                  }

                  // Show agent only if enabled
                  if (menu.label === "AI Agent") {
                    return user?.enabledAIAgent
                  }
                  // Show all other routes as is
                  return true;
                })
                .map((menu, index) => (
                  <SidebarLink
                    key={index}
                    path={menu.path}
                    icon={menu.icon}
                    label={menu.label}
                  />
                ))}
            </ul>
          </div>
        </div>

        {/* Expand / collapse button */}
        <div className="pt-3 hidden lg:inline-flex 2xl:hidden justify-between mt-auto items-center">
          {sidebarExpanded && <p className="text-white text-sm"></p>}
          <div className="px-3 py-2">
            <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>
              <span className="sr-only">Expand / collapse sidebar</span>
              <svg
                className="w-6 h-6 fill-current sidebar-expanded:rotate-180"
                viewBox="0 0 24 24"
              >
                <path
                  className="text-slate-400"
                  d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z"
                />
                <path className="text-slate-600" d="M3 23H1V1h2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  setSidebarOpen: PropTypes.func,
  sidebarOpen: PropTypes.bool
}


export default Sidebar;
